import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Box,
	Card,
	CardContent,
	Typography,
	TextField,
	Button,
	Avatar,
	Divider,
	CircularProgress,
	IconButton,
	Chip,
	Tabs,
	Tab,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Switch,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	Tooltip,
	Radio,
	RadioGroup,
	FormControlLabel,
} from "@mui/material";
import {
	CameraAlt,
	Save,
	Person,
	AdminPanelSettings,
	PersonAdd,
	Block,
	CheckCircle,
	Tune,
	Refresh,
	Key,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useSnackbar } from "notistack";
import api from "../services/api";
import ApiTokens from "../components/ApiTokens";
import { updateUser } from "../store/slices/authSlice";
import { ROLE_LABELS, canManageUsers, isAdmin as isAdminUser } from "../utils/permissions";
import { formatElapsed } from "../utils/dateUtils";

/* ─── Profile Tab ─────────────────────────────────────────── */
function ProfileTab() {
	const dispatch = useDispatch();
	const { user } = useSelector((s) => s.auth);
	const { enqueueSnackbar } = useSnackbar();
	const fileInputRef = useRef(null);

	const [form, setForm] = useState({
		firstName: user?.firstName || "",
		lastName: user?.lastName || "",
		phone: user?.phone || "",
		avatarUrl: user?.avatarUrl || "",
	});
	const [loading, setLoading] = useState(false);
	const [fetching, setFetching] = useState(true);

	useEffect(() => {
		api.get("/users/me")
			.then((res) => {
				const u = res.data;
				setForm({
					firstName: u.firstName || "",
					lastName: u.lastName || "",
					phone: u.phone || "",
					avatarUrl: u.avatarUrl || "",
				});
			})
			.catch(() => {})
			.finally(() => setFetching(false));
	}, []);

	const handleChange = (field) => (e) =>
		setForm((f) => ({ ...f, [field]: e.target.value }));

	const handleAvatarClick = () => fileInputRef.current?.click();

	const handleFileChange = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.size > 2 * 1024 * 1024) {
			enqueueSnackbar("Image must be smaller than 2 MB", { variant: "warning" });
			return;
		}
		const reader = new FileReader();
		reader.onload = (ev) =>
			setForm((f) => ({ ...f, avatarUrl: ev.target.result }));
		reader.readAsDataURL(file);
	};

	const handleSave = async () => {
		setLoading(true);
		try {
			const res = await api.put("/users/me", form);
			dispatch(updateUser(res.data));
			enqueueSnackbar("Profile updated", { variant: "success" });
		} catch {
			enqueueSnackbar("Failed to update profile", { variant: "error" });
		} finally {
			setLoading(false);
		}
	};

	const initials =
		`${form.firstName?.[0] || ""}${form.lastName?.[0] || ""}`.toUpperCase() ||
		`${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase() ||
		user?.email?.[0]?.toUpperCase() ||
		"U";

	if (fetching) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ maxWidth: 680 }}>
			<Card sx={{ mb: 3 }}>
				<CardContent sx={{ p: 3 }}>
					<Typography
						variant="h6"
						fontWeight={600}
						gutterBottom
						sx={{ display: "flex", alignItems: "center", gap: 1 }}
					>
						<Person fontSize="small" color="primary" />
						Profile
					</Typography>
					<Divider sx={{ mb: 3 }} />

					<Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
						<Box sx={{ position: "relative" }}>
							<Avatar
								src={form.avatarUrl || undefined}
								sx={{
									width: 88,
									height: 88,
									bgcolor: "primary.main",
									fontSize: "1.8rem",
									fontWeight: 700,
								}}
							>
								{!form.avatarUrl && initials}
							</Avatar>
							<IconButton
								size="small"
								onClick={handleAvatarClick}
								sx={{
									position: "absolute",
									bottom: 0,
									right: 0,
									bgcolor: "primary.main",
									color: "white",
									width: 28,
									height: 28,
									"&:hover": { bgcolor: "primary.dark" },
								}}
							>
								<CameraAlt sx={{ fontSize: 14 }} />
							</IconButton>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/*"
								hidden
								onChange={handleFileChange}
							/>
						</Box>
						<Box>
							<Typography variant="subtitle1" fontWeight={600}>
								{form.firstName || form.lastName
									? `${form.firstName} ${form.lastName}`.trim()
									: "Your Name"}
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
								{user?.email}
							</Typography>
							<Chip
								label={ROLE_LABELS[user?.role] || ROLE_LABELS.USER}
								size="small"
								color="primary"
								variant="outlined"
							/>
						</Box>
					</Box>

					<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
						<TextField
							label="First Name"
							value={form.firstName}
							onChange={handleChange("firstName")}
							fullWidth
						/>
						<TextField
							label="Last Name"
							value={form.lastName}
							onChange={handleChange("lastName")}
							fullWidth
						/>
					</Box>
				</CardContent>
			</Card>

			<Card sx={{ mb: 3 }}>
				<CardContent sx={{ p: 3 }}>
					<Typography variant="h6" fontWeight={600} gutterBottom>
						Contact Information
					</Typography>
					<Divider sx={{ mb: 3 }} />
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						<TextField
							label="Email Address"
							value={user?.email || ""}
							fullWidth
							disabled
							helperText="Email cannot be changed"
						/>
						<TextField
							label="Phone Number"
							value={form.phone}
							onChange={handleChange("phone")}
							fullWidth
							placeholder="+1 (555) 000-0000"
						/>
					</Box>
				</CardContent>
			</Card>

			<Card sx={{ mb: 3 }}>
				<CardContent sx={{ p: 3 }}>
					<Typography variant="h6" fontWeight={600} gutterBottom>
						Account
					</Typography>
					<Divider sx={{ mb: 3 }} />
					<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
						<TextField label="Role" value={ROLE_LABELS[user?.role] || ROLE_LABELS.USER} fullWidth disabled />
						<TextField
							label="Workspace"
							value={user?.currentWorkspaceId || "default"}
							fullWidth
							disabled
						/>
					</Box>
				</CardContent>
			</Card>

			<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
				<Button
					variant="contained"
					size="large"
					startIcon={
						loading ? <CircularProgress size={16} color="inherit" /> : <Save />
					}
					onClick={handleSave}
					disabled={loading}
					sx={{ px: 4 }}
				>
					{loading ? "Saving…" : "Save Changes"}
				</Button>
			</Box>
		</Box>
	);
}

/* ─── Add User Dialog ─────────────────────────────────────── */
// Admins pick any role; super users add regular users only (no role field)
function AddUserDialog({ open, onClose, onCreated, canPickRole }) {
	const { enqueueSnackbar } = useSnackbar();
	const [form, setForm] = useState({
		firstName: "",
		lastName: "",
		email: "",
		password: "",
		role: "USER",
	});
	const [loading, setLoading] = useState(false);

	const handleChange = (field) => (e) =>
		setForm((f) => ({ ...f, [field]: e.target.value }));

	const handleSubmit = async () => {
		if (!form.email || !form.password) {
			enqueueSnackbar("Email and password are required", { variant: "warning" });
			return;
		}
		setLoading(true);
		try {
			const res = await api.post("/admin/users", form);
			enqueueSnackbar(`User ${res.data.email} created`, { variant: "success" });
			onCreated(res.data);
			onClose();
			setForm({ firstName: "", lastName: "", email: "", password: "", role: "USER" });
		} catch (err) {
			enqueueSnackbar(err.response?.data?.message || "Failed to create user", { variant: "error" });
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle fontWeight={700}>Add New User</DialogTitle>
			<DialogContent>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
					<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
						<TextField
							label="First Name"
							value={form.firstName}
							onChange={handleChange("firstName")}
							fullWidth
						/>
						<TextField
							label="Last Name"
							value={form.lastName}
							onChange={handleChange("lastName")}
							fullWidth
						/>
					</Box>
					<TextField
						label="Email"
						type="email"
						value={form.email}
						onChange={handleChange("email")}
						fullWidth
						required
						helperText="Any email address works (company or personal). It will belong to this organisation only."
					/>
					<TextField
						label="Password"
						type="password"
						value={form.password}
						onChange={handleChange("password")}
						fullWidth
						required
					/>
					{canPickRole ? (
						<FormControl fullWidth>
							<InputLabel id="add-user-role-label">Role</InputLabel>
							<Select
								labelId="add-user-role-label"
								value={form.role}
								label="Role"
								onChange={handleChange("role")}
							>
								<MenuItem value="USER">{ROLE_LABELS.USER}</MenuItem>
								<MenuItem value="SUPER_USER">{ROLE_LABELS.SUPER_USER}</MenuItem>
								<MenuItem value="ADMIN">{ROLE_LABELS.ADMIN}</MenuItem>
							</Select>
						</FormControl>
					) : (
						<Typography variant="body2" color="text.secondary">
							The new account is a regular user.
						</Typography>
					)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PersonAdd />}
				>
					Create User
				</Button>
			</DialogActions>
		</Dialog>
	);
}

/* ─── Users Tab ───────────────────────────────────────────── */
// Admins: every account, with roles. Super users: regular users only (the API
// lists only those); they can add, block and set data visibility, not roles.
function UsersTab() {
	const { user: me } = useSelector((s) => s.auth);
	const admin = isAdminUser(me);
	const { enqueueSnackbar } = useSnackbar();
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [addOpen, setAddOpen] = useState(false);
	// Active users against the organisation's plan (blocked users don't count)
	const [seats, setSeats] = useState(null);

	const loadSeats = () =>
		api.get("/admin/users/seats").then((res) => setSeats(res.data)).catch(() => {});

	const load = () => {
		setLoading(true);
		api.get("/admin/users")
			.then((res) => setUsers(res.data))
			.catch(() => enqueueSnackbar("Failed to load users", { variant: "error" }))
			.finally(() => setLoading(false));
		loadSeats();
	};

	useEffect(() => { load(); }, []);

	const patch = async (id, changes) => {
		try {
			const res = await api.put(`/admin/users/${id}`, changes);
			setUsers((prev) => prev.map((u) => (u.id === id ? res.data : u)));
			if ("active" in changes) loadSeats();
			return true;
		} catch (err) {
			enqueueSnackbar(err.response?.data?.message || "Update failed", { variant: "error" });
			return false;
		}
	};

	const toggleBlock = (u) =>
		patch(u.id, { active: !u.active }).then(
			(ok) =>
				ok &&
				enqueueSnackbar(u.active ? `${u.email} blocked` : `${u.email} unblocked`, {
					variant: u.active ? "warning" : "success",
				}),
		);

	const toggleVisibility = (u) => patch(u.id, { dataVisibility: !u.dataVisibility });

	const changeRole = (u, role) => patch(u.id, { role });

	const initials = (u) =>
		`${u.firstName?.[0] || ""}${u.lastName?.[0] || ""}`.toUpperCase() ||
		u.email?.[0]?.toUpperCase() ||
		"?";

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ maxWidth: 900 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
				<Box>
					<Typography variant="h6" fontWeight={600}>
						Users ({users.length})
					</Typography>
					{seats?.limit != null && (
						<Typography variant="body2" color={seats.used >= seats.limit ? "warning.main" : "text.secondary"}>
							{seats.used} of {seats.limit} seats used (blocked users don't count)
						</Typography>
					)}
				</Box>
				<Button
					variant="contained"
					startIcon={<PersonAdd />}
					onClick={() => setAddOpen(true)}
				>
					Add User
				</Button>
			</Box>

			<Card>
				<Table>
					<TableHead>
						<TableRow sx={{ bgcolor: "background.default" }}>
							<TableCell sx={{ fontWeight: 600 }}>User</TableCell>
							{admin && <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>}
							<TableCell sx={{ fontWeight: 600 }} align="center">
								Status
							</TableCell>
							<TableCell sx={{ fontWeight: 600 }} align="center">
								<Tooltip title="When enabled, the user can see (and run and clone) everyone's projects, tests, suites and data sets. Only owners and admins edit them.">
									<span>Data Visibility</span>
								</Tooltip>
							</TableCell>
							<TableCell sx={{ fontWeight: 600 }} align="center">
								Block
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{users.map((u) => {
							const isSelf = u.id === me?.id;
							return (
								<TableRow key={u.id} hover>
									<TableCell>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
											<Avatar
												src={u.avatarUrl || undefined}
												sx={{
													width: 36,
													height: 36,
													bgcolor: "primary.main",
													fontSize: "0.85rem",
													fontWeight: 700,
												}}
											>
												{!u.avatarUrl && initials(u)}
											</Avatar>
											<Box>
												<Typography variant="body2" fontWeight={600}>
													{u.firstName || u.lastName
														? `${u.firstName || ""} ${u.lastName || ""}`.trim()
														: "—"}
													{isSelf && (
														<Chip
															label="You"
															size="small"
															sx={{ ml: 0.5, height: 18, fontSize: 10 }}
														/>
													)}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{u.email}
												</Typography>
											</Box>
										</Box>
									</TableCell>

									{admin && (
										<TableCell>
											<Tooltip title={isSelf ? "You can't change your own role" : ""}>
												<span>
													<Select
														size="small"
														value={u.role || "USER"}
														disabled={isSelf}
														onChange={(e) => changeRole(u, e.target.value)}
														sx={{ minWidth: 120 }}
														inputProps={{ "aria-label": `Role of ${u.email}` }}
													>
														<MenuItem value="USER">{ROLE_LABELS.USER}</MenuItem>
														<MenuItem value="SUPER_USER">{ROLE_LABELS.SUPER_USER}</MenuItem>
														<MenuItem value="ADMIN">{ROLE_LABELS.ADMIN}</MenuItem>
													</Select>
												</span>
											</Tooltip>
										</TableCell>
									)}

									<TableCell align="center">
										<Chip
											icon={u.active ? <CheckCircle /> : <Block />}
											label={u.active ? "Active" : "Blocked"}
											color={u.active ? "success" : "error"}
											size="small"
											variant="outlined"
										/>
									</TableCell>

									<TableCell align="center">
										<Switch
											checked={u.role === "ADMIN" ? true : !!u.dataVisibility}
											disabled={u.role === "ADMIN"}
											onChange={() => toggleVisibility(u)}
											color="primary"
											size="small"
											inputProps={{ "aria-label": `Data visibility of ${u.email}` }}
										/>
									</TableCell>

									<TableCell align="center">
										<Tooltip title={isSelf ? "Cannot block yourself" : u.active ? "Block user" : "Unblock user"}>
											<span>
												<IconButton
													size="small"
													disabled={isSelf}
													aria-label={`${u.active ? "Block" : "Unblock"} ${u.email}`}
													onClick={() => toggleBlock(u)}
													color={u.active ? "error" : "success"}
												>
													{u.active ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
												</IconButton>
											</span>
										</Tooltip>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</Card>

			<AddUserDialog
				open={addOpen}
				onClose={() => setAddOpen(false)}
				onCreated={(newUser) => {
					setUsers((prev) => [...prev, newUser]);
					loadSeats();
				}}
				canPickRole={admin}
			/>
		</Box>
	);
}

/* ─── System Tab (admins) ─────────────────────────────────── */
// System-wide settings. The backend refuses anyone but admins.

// Which queued run starts next (backend: QueuePolicy)
const POLICY_INFO = {
	FIFO: {
		label: "First come, first served",
		description: "The oldest queued run starts first, whoever started it.",
	},
	FAIR_SHARE: {
		label: "Fair share",
		description:
			"Users take turns: a free slot goes to the user with the fewest tests running, then to whoever was served least recently. A big suite can't hold up a colleague's single test.",
	},
	INTERACTIVE_FIRST: {
		label: "Interactive first",
		description:
			"Tests someone just ran by hand (single and data-driven runs) go before suite and loop tests.",
	},
	SHORTEST_FIRST: {
		label: "Shortest first",
		description:
			"Tests with the shortest average run time go first, so the queue drains faster for most people. A test that never ran counts as average.",
	},
};

const SETTING_FIELDS = [
	"maxParallelExecutions",
	"executionPolicy",
	"maxWaitMinutes",
	"maxRunningPerUser",
	"reservedInteractiveSlots",
];

const toForm = (settings) =>
	Object.fromEntries(
		SETTING_FIELDS.map((f) => [
			f,
			f === "executionPolicy" ? settings[f] : String(settings[f]),
		]),
	);

const isWhole = (v, min, max) => {
	const n = Number(v);
	return v !== "" && Number.isInteger(n) && n >= min && n <= max;
};

function SystemTab() {
	const { enqueueSnackbar } = useSnackbar();
	const [settings, setSettings] = useState(null);
	const [form, setForm] = useState(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		api.get("/admin/settings")
			.then((res) => {
				setSettings(res.data);
				setForm(toForm(res.data));
			})
			.catch((err) =>
				enqueueSnackbar(err.response?.data?.message || "Failed to load settings", { variant: "error" }),
			);
	}, [enqueueSnackbar]);

	if (!settings || !form) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
				<CircularProgress />
			</Box>
		);
	}

	const limitMax = settings.maxParallelExecutionsLimit;
	const limitValid = isWhole(form.maxParallelExecutions, 1, limitMax);
	const limit = limitValid ? Number(form.maxParallelExecutions) : limitMax;
	const valid = {
		maxParallelExecutions: limitValid,
		maxWaitMinutes: isWhole(form.maxWaitMinutes, 0, settings.maxWaitMinutesLimit),
		maxRunningPerUser: isWhole(form.maxRunningPerUser, 0, limit),
		reservedInteractiveSlots: isWhole(form.reservedInteractiveSlots, 0, limit - 1),
	};
	const allValid = Object.values(valid).every(Boolean);
	const changed = SETTING_FIELDS.some((f) => form[f] !== toForm(settings)[f]);
	const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

	const handleSave = async () => {
		setSaving(true);
		try {
			const res = await api.put("/admin/settings", {
				maxParallelExecutions: Number(form.maxParallelExecutions),
				executionPolicy: form.executionPolicy,
				maxWaitMinutes: Number(form.maxWaitMinutes),
				maxRunningPerUser: Number(form.maxRunningPerUser),
				reservedInteractiveSlots: Number(form.reservedInteractiveSlots),
			});
			setSettings(res.data);
			setForm(toForm(res.data));
			enqueueSnackbar("Settings saved", { variant: "success" });
		} catch (err) {
			enqueueSnackbar(err.response?.data?.message || "Failed to save settings", { variant: "error" });
		} finally {
			setSaving(false);
		}
	};

	return (
		<Box sx={{ maxWidth: 900 }}>
			<Card sx={{ maxWidth: 640 }}>
				<CardContent sx={{ p: 3 }}>
					<Typography variant="h6" fontWeight={600} gutterBottom>
						Test execution
					</Typography>
					<Divider sx={{ mb: 3 }} />
					<TextField
						label="Max parallel executions"
						type="number"
						value={form.maxParallelExecutions}
						onChange={set("maxParallelExecutions")}
						inputProps={{ min: 1, max: limitMax, step: 1 }}
						error={!valid.maxParallelExecutions}
						helperText={
							valid.maxParallelExecutions
								? `How many tests run at once across the whole system: single runs, data-driven rows, loops and suites, from all users. Others wait as Queued. Default ${settings.defaultMaxParallelExecutions}.`
								: `Enter a whole number from 1 to ${limitMax}`
						}
						fullWidth
					/>

					<Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
						Execution policy
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
						Which queued test starts when a slot frees up.
					</Typography>
					<RadioGroup value={form.executionPolicy} onChange={set("executionPolicy")}>
						{settings.executionPolicies.map((p) => (
							<FormControlLabel
								key={p}
								value={p}
								control={<Radio size="small" />}
								sx={{ alignItems: "flex-start", mb: 1, "& .MuiRadio-root": { pt: 0.25 } }}
								label={
									<Box>
										<Typography variant="body2" fontWeight={600}>
											{POLICY_INFO[p]?.label || p}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{POLICY_INFO[p]?.description}
										</Typography>
									</Box>
								}
							/>
						))}
					</RadioGroup>

					<Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 2 }}>
						Limits (apply under every policy)
					</Typography>
					<TextField
						label="Max wait (minutes)"
						type="number"
						value={form.maxWaitMinutes}
						onChange={set("maxWaitMinutes")}
						inputProps={{ min: 0, max: settings.maxWaitMinutesLimit, step: 1 }}
						error={!valid.maxWaitMinutes}
						helperText={
							valid.maxWaitMinutes
								? "A test queued longer than this starts next, whatever the policy, so nobody waits indefinitely. A suite running its tests one at a time counts from when the suite started. 0 = off."
								: `Enter a whole number from 0 to ${settings.maxWaitMinutesLimit}`
						}
						fullWidth
						sx={{ mb: 2 }}
					/>
					<TextField
						label="Per-user running limit"
						type="number"
						value={form.maxRunningPerUser}
						onChange={set("maxRunningPerUser")}
						inputProps={{ min: 0, max: limit, step: 1 }}
						error={!valid.maxRunningPerUser}
						helperText={
							valid.maxRunningPerUser
								? "Most tests one user can have running at once, even when slots are free. 0 = no limit."
								: `Enter a whole number from 0 to ${limit} (max parallel executions)`
						}
						fullWidth
						sx={{ mb: 2 }}
					/>
					<TextField
						label="Reserved interactive slots"
						type="number"
						value={form.reservedInteractiveSlots}
						onChange={set("reservedInteractiveSlots")}
						inputProps={{ min: 0, max: limit - 1, step: 1 }}
						error={!valid.reservedInteractiveSlots}
						helperText={
							valid.reservedInteractiveSlots
								? "Slots kept for tests run by hand, so they start quickly while suites and loops fill the rest. 0 = none."
								: `Enter a whole number from 0 to ${limit - 1} (suites keep at least one slot)`
						}
						fullWidth
					/>

					<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
						<Button
							variant="contained"
							onClick={handleSave}
							disabled={!allValid || !changed || saving}
							startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
						>
							Save
						</Button>
					</Box>
				</CardContent>
			</Card>

			<QueuePanel />
		</Box>
	);
}

const BLOCKED_LABELS = {
	suite: "Waiting for its suite",
	userLimit: "User limit",
};

// What runs and waits now, in the order it would start (GET /admin/settings/queue)
function QueuePanel() {
	const [queue, setQueue] = useState(null);
	const [error, setError] = useState(null);

	const load = () =>
		api.get("/admin/settings/queue")
			.then((res) => {
				setQueue(res.data);
				setError(null);
			})
			.catch((err) => setError(err.response?.data?.message || "Failed to load the queue"));

	useEffect(() => {
		load();
		const timer = setInterval(load, 5000);
		return () => clearInterval(timer);
	}, []);

	const kindChip = (r) => (
		<Chip
			size="small"
			variant="outlined"
			label={r.interactive ? "Interactive" : "Suite / loop"}
			color={r.interactive ? "primary" : "default"}
		/>
	);

	const runTable = (rows, queued) => (
		<Table size="small">
			<TableHead>
				<TableRow>
					{queued && <TableCell>#</TableCell>}
					<TableCell>Test</TableCell>
					<TableCell>User</TableCell>
					<TableCell>Kind</TableCell>
					<TableCell>{queued ? "Waiting" : "Running for"}</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{rows.map((r) => (
					<TableRow key={r.id}>
						{queued && <TableCell>{r.position ?? "—"}</TableCell>}
						<TableCell>
							<Typography variant="body2">{r.testName || r.id}</Typography>
							{r.group && (
								<Typography variant="caption" color="text.secondary">
									{r.group}
								</Typography>
							)}
						</TableCell>
						<TableCell>{r.userName || r.userId || "—"}</TableCell>
						<TableCell>{kindChip(r)}</TableCell>
						<TableCell>
							<Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
								{formatElapsed(r.sinceMs)}
								{r.overdue && <Chip size="small" color="warning" label="Overdue" />}
								{r.blockedBy && (
									<Chip size="small" variant="outlined" label={BLOCKED_LABELS[r.blockedBy] || r.blockedBy} />
								)}
							</Box>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);

	return (
		<Card sx={{ mt: 3 }}>
			<CardContent sx={{ p: 3 }}>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					<Typography variant="h6" fontWeight={600}>
						Queue
					</Typography>
					<Tooltip title="Refresh (updates every 5s)">
						<IconButton onClick={load} size="small">
							<Refresh />
						</IconButton>
					</Tooltip>
				</Box>
				<Divider sx={{ my: 2 }} />
				{error && (
					<Typography color="error" variant="body2" sx={{ mb: 2 }}>
						{error}
					</Typography>
				)}
				{!queue ? (
					!error && <CircularProgress size={24} />
				) : (
					<>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							{queue.running.length} of {queue.limit} slots in use · {queue.queued.length} waiting ·{" "}
							{POLICY_INFO[queue.executionPolicy]?.label || queue.executionPolicy}
						</Typography>
						{queue.users.length > 0 && (
							<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
								{queue.users.map((u) => (
									<Chip
										key={u.userId}
										size="small"
										label={`${u.userName || u.userId}: ${u.running} running, ${u.queued} waiting`}
									/>
								))}
							</Box>
						)}
						<Typography variant="subtitle2" fontWeight={600} gutterBottom>
							Running
						</Typography>
						{queue.running.length ? (
							runTable(queue.running, false)
						) : (
							<Typography variant="body2" color="text.secondary">
								Nothing running.
							</Typography>
						)}
						<Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3 }} gutterBottom>
							Waiting, in start order
						</Typography>
						{queue.queued.length ? (
							runTable(queue.queued, true)
						) : (
							<Typography variant="body2" color="text.secondary">
								Nothing waiting.
							</Typography>
						)}
					</>
				)}
			</CardContent>
		</Card>
	);
}

/* ─── Settings (root) ─────────────────────────────────────── */
export default function Settings() {
	const { user } = useSelector((s) => s.auth);
	const showUsers = canManageUsers(user);
	const showSystem = isAdminUser(user);
	const [tab, setTab] = useState("profile");

	return (
		<motion.div
			initial={{ opacity: 0, y: 16 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.3 }}
		>
			<Box sx={{ mb: 3 }}>
				<Typography variant="h4" fontWeight={700} gutterBottom>
					Settings
				</Typography>
				<Typography variant="body1" color="text.secondary">
					Manage your account details and preferences.
				</Typography>
			</Box>

			<Tabs
				value={tab}
				onChange={(_, v) => setTab(v)}
				sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
			>
				<Tab
					value="profile"
					icon={<Person fontSize="small" />}
					iconPosition="start"
					label="Profile"
				/>
				{showUsers && (
					<Tab
						value="users"
						icon={<AdminPanelSettings fontSize="small" />}
						iconPosition="start"
						label="Users"
					/>
				)}
				{showSystem && (
					<Tab
						value="system"
						icon={<Tune fontSize="small" />}
						iconPosition="start"
						label="System"
					/>
				)}
				<Tab
					value="tokens"
					icon={<Key fontSize="small" />}
					iconPosition="start"
					label="API tokens"
				/>
			</Tabs>

			{tab === "profile" && <ProfileTab />}
			{tab === "users" && showUsers && <UsersTab />}
			{tab === "system" && showSystem && <SystemTab />}
			{tab === "tokens" && <ApiTokens isAdmin={isAdminUser(user)} />}
		</motion.div>
	);
}
